import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { stats, partnerLogos } from '../../../mocks/portfolio';

gsap.registerPlugin(ScrollTrigger);

export default function StatsMarquee() {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      const counters = el.querySelectorAll<HTMLElement>('[data-counter]');
      counters.forEach((counter) => {
        const target = counter.dataset.counter || '0';
        const numeric = parseFloat(target.replace(/[^0-9.]/g, '')) || 0;
        const prefix = target.startsWith('$') ? '$' : '';
        const suffix = target.endsWith('M') ? 'M' : '';
        const obj = { val: 0 };
        gsap.to(obj, {
          val: numeric,
          duration: 1.8,
          ease: 'power3.out',
          scrollTrigger: { trigger: counter, start: 'top 85%' },
          onUpdate: () => {
            const v = obj.val >= 100 ? Math.floor(obj.val) : obj.val.toFixed(0);
            counter.textContent = `${prefix}${v}${suffix}`;
          },
        });
      });
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={ref} className="relative bg-[#070709] text-white border-y border-white/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
          {stats.map((s) => (
            <div key={s.label} className="flex flex-col items-start">
              <div
                data-counter={s.value}
                className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight"
              >
                0
              </div>
              <div className="mt-3 text-xs font-mono uppercase tracking-[0.2em] text-white/40">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Marquee logos */}
      <div className="relative border-t border-white/5 py-6 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...partnerLogos, ...partnerLogos].map((logo, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-8 text-white/30 hover:text-white/70 transition-colors font-display text-2xl tracking-tight"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white/20" />
              {logo}
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#070709] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#070709] to-transparent" />
      </div>
    </section>
  );
}